import React, { useState, useContext, useEffect } from 'react';
import { getFirestore, addDoc, collection, serverTimestamp, query, where, getDocs, getDoc, updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserProvider';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Alert from '@mui/material/Alert';

export const CreateGroupScreen = () => {
    const { userData } = useContext(UserContext);
    const navigate = useNavigate();

    const [groupName, setGroupName] = useState('');
    const [members, setMembers] = useState([]);
    const [memberEmail, setMemberEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (userData) {
            setMembers([userData]); // Initially add the current user to the members list
        }
    }, [userData]);

    const addMember = async () => {
        setErrorMessage('');
        if (memberEmail) {
            if (members.some(member => member.email === memberEmail)) {
                setErrorMessage('Member already added.');
                return;
            }

            const firestore = getFirestore();
            const usersRef = collection(firestore, "users");
            const q = query(usersRef, where("email", "==", memberEmail));
    
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                querySnapshot.forEach(doc => {
                    const userToAdd = { userId: doc.id, ...doc.data() };
                    setMembers(prev => [...prev, userToAdd]);
                });
            } else {
                setErrorMessage("No user found with the email.");
            }
            setMemberEmail('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const firestore = getFirestore();
            const groupRef = await addDoc(collection(firestore, "groups"), {
                name: groupName,
                createdAt: serverTimestamp(),
                lastModified: serverTimestamp(),
            });

            // Update members with the group's createdAt timestamp as addedAt
            const groupDoc = await getDoc(groupRef);
            const createdAtTimestamp = groupDoc.data().createdAt;
            const updatedMembers = members.map(member => ({
                ...member,
                addedAt: createdAtTimestamp,
            }));

            // Update the group with the members
            await updateDoc(groupRef, { members: updatedMembers });

            navigate(`/groups/${groupRef.id}`);
        } catch (error) {
            console.error("Error creating group:", error);
            setErrorMessage("Failed to create group.");
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{ maxWidth: 400, margin: 'auto', padding: '20px' }}>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Group Name"
                    variant="outlined"
                    fullWidth
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    margin="normal"
                />
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <TextField
                        label="Member Email"
                        variant="outlined"
                        value={memberEmail}
                        onChange={(e) => setMemberEmail(e.target.value)}
                        type="email"
                        style={{ flex: 1, marginRight: 8 }}
                    />
                    <Button variant="contained" onClick={addMember}>Add</Button>
                </div>
                {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}
                <List>
                    {members.map((member, index) => (
                        <ListItem key={index}>{member.displayName} ({member.email})</ListItem>
                    ))}
                </List>
                <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    fullWidth
                    disabled={isSubmitting || members.length < 2 || groupName.length < 2}
                >
                    Create Group
                </Button>
            </form>
        </div>
    );
};
